import { useEffect, useRef } from 'react';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { useConfirmationDialog } from '../../../shared/ui/dialogs/useConfirmationDialog';
import { DEFAULT_PIPEEX_PARAMETERS_CONSENT } from '../../../shared/ui/workflow/defaultPipeexParametersConsent';

/**
 * Standard-parameters prompt. Saving/applying here builds the profile via
 * `buildPcfSettingsDtoFromStore`, which — beyond what the setup wizard
 * covered — merges the shipped PipeEX standard parameter set into the
 * profile. Until the user answers, it is undecided whether that set belongs
 * in their profile, so the first run asks explicitly. Both answers persist
 * through `includeDefaultPipeexParameters` (saved into and restored from the
 * profile); the DTO builder treats `null` (never asked) as "include" so
 * profiles saved before the prompt keep their legacy content.
 *
 * The answer rides inside the saved profile, so on a landing the prompt must
 * wait until the profile restore/load has had its chance to bring the answer
 * back — `enabled` gates the ask. Firing on mount (before restore) re-asked
 * the already-answered question on every refresh. The wizard mount
 * (ProcessPropertiesPage) passes no gate: the profile does not exist yet.
 */
export const useIncludeDefaultPipeexParametersPrompt = (options?: {
  enabled?: boolean;
  suppressAndExclude?: boolean;
  onIncludeConfirmed?: () => void;
}) => {
  // Optional chaining: partially mocked stores (test suites) have no
  // processPropertiesConfiguration at all — `undefined` must behave as
  // "already answered" and suppress the prompt; only a strict `null` (real
  // store, not yet asked) opens it.
  const choice = useConfigurationStore(
    (state) => state.processPropertiesConfiguration?.includeDefaultPipeexParameters
  );
  const setChoice = useConfigurationStore((state) => state.setIncludeDefaultPipeexParameters);
  const { requestConfirmation, confirmationDialog } = useConfirmationDialog();
  const promptRequestedRef = useRef(false);
  const enabled = options?.enabled !== false;
  const suppressAndExclude = options?.suppressAndExclude === true;
  const onIncludeConfirmedRef = useRef(options?.onIncludeConfirmed);
  onIncludeConfirmedRef.current = options?.onIncludeConfirmed;

  useEffect(() => {
    if (suppressAndExclude) {
      if (choice === null) {
        setChoice(false, { markDirty: false });
      }
      return;
    }
    if (!enabled || choice !== null || promptRequestedRef.current) {
      return;
    }
    // The ref survives the StrictMode double-effect cycle, so the dialog is
    // requested exactly once per unanswered landing.
    promptRequestedRef.current = true;

    void requestConfirmation({
      title: DEFAULT_PIPEEX_PARAMETERS_CONSENT.title,
      message: DEFAULT_PIPEEX_PARAMETERS_CONSENT.message,
      detailsLabel: DEFAULT_PIPEEX_PARAMETERS_CONSENT.detailsLabel,
      details: DEFAULT_PIPEEX_PARAMETERS_CONSENT.details,
      confirmLabel: DEFAULT_PIPEEX_PARAMETERS_CONSENT.confirmLabel,
      cancelLabel: DEFAULT_PIPEEX_PARAMETERS_CONSENT.cancelLabel,
      confirmColor: DEFAULT_PIPEEX_PARAMETERS_CONSENT.confirmColor,
    }).then((include) => {
      setChoice(include);
      // "Include" also consents to writing the missing standard parameters
      // into the active Revit project (the repair action the readiness gate
      // would otherwise wait for).
      if (include) {
        onIncludeConfirmedRef.current?.();
      }
    });
  }, [choice, enabled, requestConfirmation, setChoice, suppressAndExclude]);

  return { defaultParametersPromptDialog: confirmationDialog };
};
